/**
 * Proof-of-work solver.
 *
 * The browser finds a nonce whose SHA-256 has N leading zero bits before a
 * session will open. Costs a real visitor a few hundred milliseconds they don't
 * perceive; costs someone running the assessment repeatedly that same work
 * every single time. See docs/BOT_DEFENCE.md for why this rather than a puzzle
 * the visitor solves.
 *
 * Uses a synchronous SHA-256 rather than crypto.subtle.digest. That looks
 * backwards — Web Crypto is native — but subtle.digest returns a Promise, so a
 * solving loop pays an event-loop round trip per hash. Measured on a throttled
 * mid-range phone that capped throughput near 46k hashes/sec, which made
 * difficulty 18 take six seconds and time out on an older device. The tight
 * synchronous loop is what makes the difficulty setting mean anything.
 */

export interface Challenge {
  challenge: string;
  issued_at: number;
  signature: string;
  difficulty: number;
}

export interface Solution extends Challenge {
  solution: string;
}

import { sha256 } from "./sha256";

const encoder = new TextEncoder();

function leadingZeroBits(bytes: Uint8Array): number {
  let bits = 0;
  for (const byte of bytes) {
    if (byte === 0) {
      bits += 8;
      continue;
    }
    bits += 8 - (32 - Math.clz32(byte));
    break;
  }
  return bits;
}

export async function fetchChallenge(): Promise<Challenge> {
  const response = await fetch("/api/mini/challenge");
  if (!response.ok) throw new Error("Could not start the assessment.");
  return response.json();
}

/**
 * Find a valid nonce.
 *
 * `onProgress` reports 0..1 against the *expected* number of attempts, so the
 * animation has something honest to advance on. It's a statistical estimate,
 * not a real percentage — hashing is memoryless, so the true remaining work is
 * the same after a million attempts as it was at the start. The progress bar
 * therefore eases toward 1 without reaching it, and completion is the only
 * thing that actually lands it.
 */
export async function solveChallenge(
  challenge: Challenge,
  onProgress?: (fraction: number) => void,
): Promise<Solution> {
  const expected = Math.pow(2, challenge.difficulty);
  let attempt = 0;

  // Yield to the event loop periodically or the main thread freezes and the
  // animation — the entire point of doing this visibly — never paints. Sized so
  // a batch is a few milliseconds even on a slow phone.
  const BATCH = 3000;

  for (;;) {
    for (let i = 0; i < BATCH; i++) {
      const candidate = String(attempt++);
      const digest = sha256(encoder.encode(`${challenge.challenge}:${candidate}`));
      if (leadingZeroBits(digest) >= challenge.difficulty) {
        onProgress?.(1);
        return { ...challenge, solution: candidate };
      }
    }

    // Asymptote at 0.95 so the bar never sits full while still working.
    onProgress?.(Math.min(0.95, 1 - Math.exp(-attempt / expected)));
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}
